import { CommonModule } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, Output, inject } from "@angular/core";

import { EvaButtonComponent } from "src/app/shared/ds/eva-button/eva-button.component";
import { renderMarkdown } from "src/app/shared/utils/markdown";

import { AiFeedbackComponent } from "../ai-feedback/ai-feedback.component";
import { ChatMessage, FeedbackVote, PendingAction } from "../services/ai-chat.service";

export interface MessageView {
  role: "user" | "assistant";
  html: string;
  photos: number;
  documents: string[];
  error: boolean;
  turnId?: string;
  feedback: FeedbackVote | null;
}

export function toMessageView(message: ChatMessage): MessageView {
  return {
    role: message.role,
    html: renderMarkdown(message.content),
    photos: message.photos ?? 0,
    documents: message.documents ?? [],
    error: message.error ?? false,
    turnId: message.turn_id,
    feedback: message.feedback ?? null,
  };
}

/**
 * Fil de l'assistant, commun à la page /ai, à l'accueil et au galet : messages, carte de
 * validation et progression. L'élément hôte est la zone qui défile ; le contenu projeté
 * s'affiche quand il n'y a encore aucun message.
 */
/**
 * Au-delà, proposer une nouvelle conversation : l'agent web ne garde que 20 tours
 * (`web_max_history_turns`) et renvoie tout l'historique à chaque question, donc plus lent et plus cher.
 */
export const LONG_CONVERSATION_QUESTIONS = 15;

export function isLongConversation(messages: MessageView[]): boolean {
  return messages.filter((m) => m.role === "user").length >= LONG_CONVERSATION_QUESTIONS;
}

@Component({
  selector: "app-ai-thread",
  standalone: true,
  imports: [CommonModule, EvaButtonComponent, AiFeedbackComponent],
  templateUrl: "./ai-thread.component.html",
  styleUrls: ["./ai-thread.component.scss"],
  host: { "aria-live": "polite" },
})
export class AiThreadComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() messages: MessageView[] = [];

  /** Clé du fil : le tour quand il est connu, sinon la position. `??` directement dans `track`
   *  plante à l'exécution en Angular 17.3 (variable temporaire non déclarée). */
  trackMessage(index: number, message: MessageView): string | number {
    return message.turnId ?? index;
  }
  /** Conversation affichée : le 👍/👎 y rattache son vote. */
  @Input() conversationId: string | null = null;
  @Input() pending: PendingAction | null = null;
  @Input() busy = false;
  /** Libellé de l'étape en cours (« Réflexion… », « Recherche dans EVA… ») ; vide = rien en cours. */
  @Input() progress = "";
  @Output() decided = new EventEmitter<"valider" | "refuser">();
  /** « Nouvelle conversation » proposée quand la conversation devient trop longue. */
  @Output() restart = new EventEmitter<void>();

  get long(): boolean {
    return !this.busy && !this.pending && isLongConversation(this.messages);
  }

  scrollToBottom(): void {
    setTimeout(() => {
      const el = this.host.nativeElement;
      el.scrollTop = el.scrollHeight;
    });
  }
}
